"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const thesisPoints = [{
  number: "01",
  title: "Energy-as-a-Service (EaaS)",
  description: "Hybrid grid infrastructure combined with long-term power contracts and renewable PPAs — securing 24/7 scalable power capacity with ESG-aligned infrastructure design and energy cost optimization."
}, {
  number: "02",
  title: "Strategic Land Development",
  description: "AI-suitable parcels of 10–50+ acres sourced near substations and fiber corridors through AI-driven site selection aligned with grid capacity, utility coordination, and permitting acceleration."
}, {
  number: "03",
  title: "AI-Optimized Modular Build",
  description: "Liquid-cooled, future-proof 40–150kW rack-ready facilities built in 20–40MW modular phases for H100/GB300 chipsets, cluster architectures, and next-generation cooling standards."
}, {
  number: "04",
  title: "Network & Services Layer",
  description: "High-bandwidth, sovereign-ready interconnect with GPU infrastructure alignment, structured compute monetization models, and flexible dedicated/shared cluster strategies."
}];
function ThesisPoint({
  number,
  title,
  description,
  index
}: {
  number: string;
  title: string;
  description: string;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div ref={ref} data-index={index} className={`group grid gap-6 border-t border-[#1C1917]/20 py-10 transition-all duration-700 ease-out md:grid-cols-[90px_1fr] md:gap-10 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="font-mono text-sm font-bold tracking-[0.25em] text-[#B45309]">
        {number}
      </div>

      <div>
        <h3 className="font-serif text-3xl font-bold tracking-tight text-[#1C1917] md:text-4xl">
          {title}
        </h3>

        <p className="mt-4 max-w-3xl text-base leading-relaxed text-[#1C1917]/65 md:text-lg">
          {description}
        </p>
      </div>
    </div>;
}
export default function TheIntelligenceSupercycle() {
  return <section id="the-intelligence-supercycle" className="relative overflow-hidden bg-[#FDFAF3] py-24 text-[#1C1917] md:py-32">
      {/* Top-left glow shadow */}
      <div className="pointer-events-none absolute left-0 top-0 h-[620px] w-[620px] -translate-x-1/3 -translate-y-1/3 rounded-full bg-[#E7D8B8]/55 blur-[100px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-16 max-w-4xl">
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-9 bg-[#B45309]" />
            <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#B45309]">
              Execution Control
            </span>
          </div>

          <h2 className="font-serif text-5xl font-bold leading-[1.05] tracking-tight text-[#1C1917] md:text-7xl">
            N2N Controls the{" "}
            <span className="text-[#B45309]">Critical Execution Layers</span>
          </h2>

          <p className="mt-8 max-w-3xl text-lg leading-relaxed text-[#1C1917]/65 md:text-xl">
            N2N Capital controls critical infrastructure layers to reduce
            execution risk and enhance return durability. Execution control
            across power, land, hardware, and deployment enhances IRR
            predictability and valuation resilience.
          </p>
        </div>

        <div className="rounded-2xl border border-[#E7D8B8] bg-[#F5EDD8]/55 p-6 shadow-sm md:p-10">
          <div className="ml-auto max-w-5xl">
            {thesisPoints.map((point, index) => <ThesisPoint key={point.number} index={index} number={point.number} title={point.title} description={point.description} />)}
          </div>
        </div>
      </div>
    </section>;
}